<template>
  <div class="stores">
    <div class="container">
      <block-breadcrumbs />

      <div class="head">
        <h1 class="title">
          Адреса магазинов в городе
          <span @click="changeCity">
            {{ currentCity.name || CITY.name }}
          </span>
        </h1>
        <div
          v-if="metroSpecs.length"
          class="select"
        >
          <ui-select
            :value="metro"
            :options="sortedMetroSpecs"
            empty-value="Не выбрана"
            placeholder="Выберите станцию метро"
            @change="onChangeMetro"
          />
        </div>
        <ui-segmented
          v-model="view"
          class="tabs"
          :labels="['Список', 'Карта']"
        />
        <h2>
          {{ stores.length }} {{ stores.length | decl('магазин', ['', 'а', 'ов']) }}
        </h2>
      </div>

      <div class="body">
        <stores-list
          v-if="view === 'Список'"
          :stores="stores"
        />
        <stores-map
          v-else
          :active-store-id="activeStoreId"
          :current-local-city="currentCity"
          popup-by-click
          class="map"
          :stores="stores"
          @change="activeStoreId = $event"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
/* Components */
import blockBreadcrumbs from '../components/breadcrumbs/breadcrumbs';
import StoresList from '../components/stores/stores-list';
import StoresMap from '../components/stores/stores-map';
import { getGeoCoords } from '@/utils/geo-utils';
import pageMetaMixin from '@/mixins/page-meta-mixin';

export default {
  name: 'stores',

  metaInfo() {
    return this.getPageMeta({ pageTitle: 'Адреса магазинов' });
  },

  components: {
    StoresMap,
    blockBreadcrumbs,
    StoresList,
  },

  mixins: [pageMetaMixin],

  data() {
    return {
      view: 'Список',
      metro: '',
      currentCity: {},
      filteredStores: [],
      activeStoreId: '',
      userGeoCoords: null,
    };
  },

  computed: {
    ...mapGetters({
      STORES: 'stores/stores',
      CITY: 'user/city'
    }),

    stores() {
      if (this.filteredStores.length) return this.filteredStores;
      return this.STORES;
    },

    metroSpecs() {
      return this.STORES.reduce((acc, store) => {
        store.metro.forEach(metro => {
          if (!acc.some(m => m.name === metro.name && m.line.color === metro.line.color)) acc.push(metro)
        });
        return acc;
      }, []);
    },

    sortedMetroSpecs() {
      return [...this.metroSpecs].sort((a, b) => {
        if (a.name < b.name) { return -1; }
        if (a.name > b.name) { return 1; }
        return 0;
      });
    },

    storeSorting() {
      if (!this.userGeoCoords) return;
      const { latitude, longitude } = this.userGeoCoords;
      return `location:${latitude},${longitude}`;
    }
  },

  async mounted() {
    try {
      // при показе компонента, пытаемся получить геопозицию пользователя
      this.userGeoCoords = await getGeoCoords();
      // если не удалось получить геопозицию, то не запрашиваем сортированный список магазинов
      if (!this.userGeoCoords) return;
      this.LOAD_STORES({ sort: this.storeSorting });
    } catch (e) {
      this.userGeoCoords = null;
    }
  },

  methods: {
    ...mapActions({
      LOAD_STORES: 'stores/loadStores'
    }),

    changeCity() {
      this.$city.changeCity().then(city => {
        this.LOAD_STORES({
          cityId: city.id,
          sort: this.storeSorting,
        }).then(() => {
          this.onChangeMetro({name: '', value: ''});
          this.currentCity = {
            name: city.name,
            geo: city.geo,
          };
        });
      }).catch(() => {});
    },

    onChangeMetro(metro) {
      this.metro = metro?.value === this.metro ? '' : metro?.value;
      this.filteredStores = this.STORES.filter(store => {
          return store.metro.some(m => m.name === this.metro);
      })
    }
  },

  serverPrefetch() {
    return this.LOAD_STORES()
  }
};
</script>

<style lang="scss">
.stores {
  .head {
    display: flex;
    flex-direction: column;
    margin-top: 20px;

    & > .tabs {
      width: 100%;
      margin-top: 12px;
    }

    .title {
      font-weight: normal;
      font-size: 20px;
      line-height: 28px;

      span {
        color: $blue;
        cursor: pointer;
      }
    }

    h2 {
      margin-top: 24px;
      margin-bottom: 0;
      font-family: Gilroy, sans-serif;
      font-size: 16px;
      line-height: 24px;
    }

    .select {
      margin-top: 16px;
    }
  }

  .body {
    .map {
      height: 480px;
      margin-top: 16px;
    }

    .list {
      margin-bottom: -80px;

      & > .item:last-of-type {
        border-bottom: 0;
      }
    }
  }
}

@include breakpoint("tablet") {
  .stores {
    .head {
      flex-direction: row;
      align-items: flex-end;
      flex-wrap: wrap;
      margin-top: 12px;

      & > .tabs {
        margin-top: 24px;
      }

      .title {
        font-size: 24px;
        line-height: 32px;
      }

      h2 {
        margin-top: 32px;
        font-size: 16px;
        line-height: 24px;
      }

      .select {
        width: 180px;
        margin-top: 0;
        margin-left: auto;
      }
    }

    .body {
      .map {
        height: 580px;
        margin-top: 20px;
      }

      .list {
        margin-bottom: -24px;

        & > .item:last-of-type {
          border-bottom: 1px solid $gray_lu;
        }
      }
    }
  }
}

@include breakpoint("desktop") {
  .stores {
    .head {
      justify-content: space-between;
      align-items: center;
      margin-top: 32px;

      & > .tabs {
        width: auto;
        margin-top: 0;
        margin-left: 12px;
      }

      .title {
        font-size: 32px;
        line-height: 48px;
      }

      h2 {
        width: 100%;
        margin-top: 40px;
        font-size: 20px;
        line-height: 28px;
      }

      .select {
        width: 240px;
      }
    }

    .body {
      .map {
        margin-top: 24px;
      }

      .list {
        margin-bottom: -14px;
      }
    }
  }
}
</style>
